import React, {
    type FC,
    memo,
    type PropsWithChildren,
    Suspense,
    use,
    useEffect,
    useMemo,
    ViewTransition,
    type DragEvent,
} from "react";
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    Outlet,
    Route,
    Routes,
    useHref,
    useLocation,
    useNavigate,
} from "react-router-dom";

import "./App.css";

import { Header } from "appearance/components/header/Header";
import { AppSettingsContext } from "@domain/context/AppSettingsProvider";
import { AvvAcceptanceModal } from "@app/components/company/AvvAcceptanceModal";
import { ErrorBoundary } from "@domain/routing/ErrorBoundary";
import { ErrorView } from "@app/pages/static/ErrorViews";
import { SkeletonPage } from "@domain/routing/AuthWrapper";
import { routeSwitch } from "@app/routing/routes";
import { ReloadPrompt } from "@ui-components/ReloadPrompt";
import { ReleaseNotesProvider } from "@domain/context/ReleaseNotesProvider";
import { useIntl } from "react-intl";
import { AbstractTableColumns } from "@ui-components/AgGridTable/columns/AbstractTableColumns";
import { RouterProvider, Toast } from "@heroui/react";
import { AgentRunProvider } from "@app/pages/home/agent/AgentRunProvider";

const preventDefault = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

// Same tree the router renders, so a route's id identifies the page that the fade keys on.
const routeObjects = createRoutesFromChildren(routeSwitch);

// HeroUI links navigate through react-router instead of reloading the page.
const HeroUiRouter: FC<PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();
    return (
        <RouterProvider navigate={navigate} useHref={useHref}>
            {children}
        </RouterProvider>
    );
};

const AppImpl: FC = () => (
    <BrowserRouter>
        <HeroUiRouter>
            <AnimatedApp />
            <ReloadPrompt />
        </HeroUiRouter>
    </BrowserRouter>
);

const AnimatedApp: FC = () => {
    // const location = useLocation();

    return (
        <Routes>
            <Route element={<AppLayout />}>{routeSwitch}</Route>
        </Routes>
    );
};
const AppLayout: FC = () => {
    const { appError, setAppError } = use(AppSettingsContext);
    const { pathname } = useLocation();
    const routeKey = useMemo(() => matchRoutes(routeObjects, pathname)?.[0]?.route.id ?? "", [pathname]);

    const intl = useIntl();

    useEffect(() => {
        AbstractTableColumns.setIntl = intl;
    }, [intl]);

    return (
        <AgentRunProvider>
            <div className="App__container" onDrop={preventDefault} onDragOver={preventDefault}>
                <ReleaseNotesProvider>
                    <Header />
                </ReleaseNotesProvider>
                <AvvAcceptanceModal />
                <div className="h-full overflow-hidden">
                    <Toast.Provider placement="top" />
                    <ErrorBoundary setError={setAppError}>
                        {Boolean(appError) && <ErrorView />}
                        {!appError && (
                            <Suspense fallback={<SkeletonPage />}>
                                {/* Keyed enter/exit, never `update`: an update fade fires on every in-page startTransition. */}
                                <ViewTransition key={routeKey} default="none" enter="route-fade" exit="route-fade">
                                    <Outlet />
                                </ViewTransition>
                            </Suspense>
                        )}
                    </ErrorBoundary>
                </div>
            </div>
        </AgentRunProvider>
    );
};

export const App = memo(AppImpl);
